import type { Fluxo, FluxoBlock } from "@/hooks/useFluxos";

/**
 * Pausa entre um bloco e o próximo. Mesmo com envios sequenciais o WhatsApp pode
 * entregar mensagens fora de ordem quando elas saem em rajada.
 */
export const FLUXO_BLOCK_INTERVAL_MS = 1000;

export interface FluxoProgress {
  current: number;
  total: number;
  block: FluxoBlock;
}

export interface FluxoRunnerHandlers {
  sendText: (content: string) => Promise<boolean>;
  sendImage: (source: string, fileName?: string) => Promise<boolean>;
  sendVideo: (source: string, fileName?: string) => Promise<boolean>;
  sendDocument: (source: string, fileName?: string, mimeType?: string) => Promise<boolean>;
  sendAudio: (source: string, fileName?: string) => Promise<boolean>;
  activateAssistant: (assistantId: string) => Promise<boolean>;
  applyTags: (tags: string[]) => Promise<boolean>;
  onProgress?: (progress: FluxoProgress) => void;
  shouldAbort?: () => boolean;
}

export type FluxoRunStatus = "completed" | "failed" | "aborted" | "empty";

export interface FluxoRunResult {
  status: FluxoRunStatus;
  /** Blocos executados com sucesso. */
  sent: number;
  total: number;
  error?: string;
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Blocos incompletos (texto vazio, mídia sem arquivo) são ignorados no envio. */
export const isRunnableBlock = (block: FluxoBlock): boolean => {
  switch (block.type) {
    case "texto":
      return block.content.trim().length > 0;
    case "imagem":
    case "video":
    case "arquivo":
    case "audio":
      return block.url.trim().length > 0;
    case "delay":
      return block.seconds > 0;
    case "assistente":
      return block.assistantId.trim().length > 0;
    case "tag":
      return block.tags.some((tag) => tag.trim().length > 0);
  }
};

const describeBlock = (block: FluxoBlock): string => {
  switch (block.type) {
    case "texto":
      return "texto";
    case "imagem":
      return "imagem";
    case "video":
      return "vídeo";
    case "arquivo":
      return "arquivo";
    case "audio":
      return "áudio";
    case "delay":
      return "espera";
    case "assistente":
      return "assistente";
    case "tag":
      return "tag";
  }
};

const executeBlock = async (
  block: FluxoBlock,
  handlers: FluxoRunnerHandlers
): Promise<boolean> => {
  switch (block.type) {
    case "texto":
      return handlers.sendText(block.content);
    case "imagem":
      return handlers.sendImage(block.url, block.fileName);
    case "video":
      return handlers.sendVideo(block.url, block.fileName);
    case "arquivo":
      return handlers.sendDocument(block.url, block.fileName, block.mimeType);
    case "audio":
      return handlers.sendAudio(block.url, block.fileName);
    case "delay":
      await sleep(block.seconds * 1000);
      return true;
    case "assistente":
      return handlers.activateAssistant(block.assistantId);
    case "tag":
      return handlers.applyTags(block.tags);
  }
};

/**
 * Executa os blocos de um fluxo em sequência, parando no primeiro erro para não
 * entregar um fluxo pela metade e fora de contexto.
 */
export const runFluxo = async (
  fluxo: Fluxo,
  handlers: FluxoRunnerHandlers
): Promise<FluxoRunResult> => {
  const blocks = fluxo.blocks.filter(isRunnableBlock);
  const total = blocks.length;

  if (total === 0) {
    return { status: "empty", sent: 0, total: 0 };
  }

  let sent = 0;

  for (let index = 0; index < total; index += 1) {
    if (handlers.shouldAbort?.()) {
      return { status: "aborted", sent, total };
    }

    const block = blocks[index];
    handlers.onProgress?.({ current: index + 1, total, block });

    try {
      const ok = await executeBlock(block, handlers);
      if (!ok) {
        return {
          status: "failed",
          sent,
          total,
          error: `Falha ao enviar o bloco de ${describeBlock(block)}`,
        };
      }
    } catch (err) {
      return {
        status: "failed",
        sent,
        total,
        error:
          err instanceof Error
            ? err.message
            : `Falha ao enviar o bloco de ${describeBlock(block)}`,
      };
    }

    sent += 1;

    const next = blocks[index + 1];
    if (next && block.type !== "delay" && next.type !== "delay") {
      await sleep(FLUXO_BLOCK_INTERVAL_MS);
    }
  }

  return { status: "completed", sent, total };
};
